import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';

const OnboardingWelcome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // If user already completed onboarding, redirect
    if (user?.profile?.onboarding_completed) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleContinue = async () => {
    setIsLoading(true);
    try {
      // Update onboarding step
      await authService.updateProfile({
        onboarding_step: 'business-setup'
      });
      navigate('/onboarding/business-setup');
    } catch (error) {
      console.error('Update onboarding step error:', error);
      toast.error('Failed to continue. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <svg className="w-6 h-6 mr-3 text-primary" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
            <span className="font-instrument text-3xl">QuoteTier</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto">
          {/* Welcome Message */}
          <div className="mb-12">
            <h1 className="font-instrument text-5xl sm:text-6xl tracking-tight leading-tight mb-6">
              Welcome to <span className="text-primary font-bold">QuoteTier!</span>
            </h1>
            <p className="font-jetbrains text-xl text-gray-600 leading-relaxed">
              Let's get your business set up in just 2 minutes. You'll be creating professional quotes that convert better than traditional PDFs.
            </p>
          </div>

          {/* Benefits Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="border border-black rounded-lg p-6 bg-white">
              <div className="bg-primary/10 text-primary h-12 w-12 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3h18v18H3zM9 9h6v6H9z"/>
                </svg>
              </div>
              <h3 className="font-instrument text-xl mb-2">Professional Branding</h3>
              <p className="font-jetbrains text-gray-600 text-sm">Upload your logo and create branded quotes that build trust</p>
            </div>
            
            <div className="border border-black rounded-lg p-6 bg-white">
              <div className="bg-primary/10 text-primary h-12 w-12 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </div>
              <h3 className="font-instrument text-xl mb-2">Interactive Quotes</h3>
              <p className="font-jetbrains text-gray-600 text-sm">Let customers explore options and choose higher-value packages</p>
            </div>
            
            <div className="border border-black rounded-lg p-6 bg-white">
              <div className="bg-primary/10 text-primary h-12 w-12 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 10.5c-1 .5-2 1-3 1s-2-.5-3-1-2-1-3-1-2 .5-3 1-2 1-3 1"/>
                  <path d="M2 13.5c1 .5 2 1 3 1s2-.5 3-1 2-1 3-1 2 .5 3 1 2 1 3 1"/>
                </svg>
              </div>
              <h3 className="font-instrument text-xl mb-2">Electronic Signatures</h3>
              <p className="font-jetbrains text-gray-600 text-sm">Get instant approvals with legally binding electronic signatures</p>
              <p className="font-jetbrains text-gray-500 text-xs mt-2">*Legally binding under ESIGN Act & UETA</p>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm font-medium">1</div>
                <span className="font-jetbrains ml-2 text-sm text-gray-600">Welcome</span>
              </div>
              <div className="w-8 h-px bg-gray-300"></div>
              <div className="flex items-center">
                <div className="w-8 h-8 border-2 border-gray-300 text-gray-400 rounded-full flex items-center justify-center text-sm font-medium">2</div>
                <span className="font-jetbrains ml-2 text-sm text-gray-400">Business Setup</span>
              </div>
              <div className="w-8 h-px bg-gray-300"></div>
              <div className="flex items-center">
                <div className="w-8 h-8 border-2 border-gray-300 text-gray-400 rounded-full flex items-center justify-center text-sm font-medium">3</div>
                <span className="font-jetbrains ml-2 text-sm text-gray-400">Trial Setup</span>
              </div>
            </div>
          </div>

          {/* Continue Button */}
          <div className="space-y-4">
            <button
              onClick={handleContinue}
              disabled={isLoading}
              className="font-instrument text-xl w-full sm:w-auto px-8 py-4 bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="spinner spinner-sm text-white border-white/30 mr-2"></div>
                  Starting Setup...
                </div>
              ) : (
                'Start Business Setup'
              )}
            </button>
            <p className="font-jetbrains text-sm text-gray-500">
              Takes less than 2 minutes
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OnboardingWelcome;
